import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "../../context/session-context.service";
import { SessionService } from "../../services/session.service";
import { DataProvider } from "../../providers/data.provider";
import packageJson from "../../../../../../package.json";
import { Location } from "@angular/common";
import { Message } from "../../../../../shared-components/src/lib/models/message.model";
import { SpinnerService } from "shared-components";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subscription, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
})
export class HeaderComponent implements OnInit, OnDestroy {
  public loginModal = false;
  public logoutModal = false;
  public chngPsswdCloseModal = true;
  public userEmail: any;
  public userGuid: any;
  public defaultLanguage;
  public readonly languages = [
    { cultureInfo: "es-ES", des: "", classIcon: "flag-espana" },
    { cultureInfo: "en-US", des: "", classIcon: "flag-usa" },
    { cultureInfo: "en-GB", des: "", classIcon: "flag-gb" },
  ];
  public optionsLanguage = [];
  public selectedLanguage;
  public fecha = "2026-10-08T10:00:00";

  public showOptions = false;
  public showAppInfoModal = false;
  public showChangePasswordModal = false;
  public showChangeLanguageModal = false;
  public showLanguageOptions = false;
  public showChangeLanguage = false;
  public showEmailSubscriptionModal = false;
  public showOptionsDownload = false;
  public showNotifications = false;
  public refreshedNotifications;
  public pendingNotifications;
  private notificationSubscription: Subscription;
  private readonly destroy$ = new Subject<void>();

  public showCustomStylesModal = false;
  public customStylesForm: FormGroup;
  private gesvatecColors = {
    primaryColor: "#004434",
    secondaryColor: "#A39B4B",
    tertiaryColor: "#edf2f0", // Realmente es #184d3913 pero el input no lo puede representar
    tableHeaderColor: "#004434",
    subtableHeaderColor: "#28564f",
  };
  private customStylesToSave = {
    id: null,
    primaryColor: null,
    secondaryColor: null,
    tertiaryColor: null,
    tableHeaderColor: null,
    subtableHeaderColor: null,
    centralLogo: null, // Para las img, usaremos un objeto con las propiedades name y src
    topBarLogo: null,
    spinnerLogo: null,
    spinnerDashboardLogo: null,
  }
  private updateInitialCustomStylesValues = false; // Para actualizar los valores del form solo al abrir el modal
  // Al resetear el color principal/cabecera tablas, no resetear también los relacionados
  private avoidResettingRelatedColors = false;

  public showError = false;
  public mensajes = new Array<Message>();
  public toCloseSpinner = 0;

  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('fileInput2') fileInput2: ElementRef;
  @ViewChild('fileInput3') fileInput3: ElementRef;
  @ViewChild('fileInput4') fileInput4: ElementRef;

  @HostListener("document:click", ["$event"])
  clickout(event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showOptions = false;
      this.showNotifications = false;
    }
  }

  constructor(
    public readonly _router: Router,
    private readonly _spinnerService: SpinnerService,
    private eRef: ElementRef,
    private readonly _translate: TranslateService,
    private readonly _sessionContext: SessionContextService,
    private readonly _sessionService: SessionService,
    private readonly _dataProvider: DataProvider,
    private readonly _location: Location,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.notificationSubscription = this._sessionContext.pendingNotifications.subscribe(newValue => {
      this.pendingNotifications = newValue;
    });

    this.customStylesForm = this.fb.group({
      primaryColor: this.gesvatecColors.primaryColor,
      secondaryColor: this.gesvatecColors.secondaryColor,
      tertiaryColor: this.gesvatecColors.tertiaryColor,
      tableHeaderColor: this.gesvatecColors.tableHeaderColor,
      subtableHeaderColor: this.gesvatecColors.subtableHeaderColor,
      centralLogo: null,
      topBarLogo: null,
      spinnerLogo: null,
      spinnerDashboardLogo: null,
    });

    this.loadLanguage(this._translate.getDefaultLang());

    if (!this._location.path().includes("/trz?q")) {
      this.loginModal = true;
    }

    // En caso de que se refresque la página, hay que recuperar las posibles notificaciones pendientes si la sesión está activa
    if (this.isSessionActive && this.role !== 'Administrador' && this.claims.includes('D_CNO')) {
      this._sessionService.getNotificacionesPendientes(4).toPromise().finally(() => {
      }).then(
        (resp) => {
          if (resp) {
            const newPendingNotifications = resp.map(object => {
              return {
                ...object,
                enviadoHaceFormateado: this.prepareNotificationEnviadoHace(object.enviadoHace_PeriodoTiempo)
              }
            });
            this._sessionContext.pendingNotifications.next(newPendingNotifications);
          }
        }
      )
    }

    // En cuanto se elija otro color principal, se cambia el valor del terciario y el de la cabecera de las tablas
    this.customStylesForm.get('primaryColor').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(color => {
      if (this.avoidResettingRelatedColors) {
        this.avoidResettingRelatedColors = false;
        return;
      }
      if (color && color.length === 7) { // 7: valor hexadecimal
        this.customStylesForm.get('tableHeaderColor').patchValue(color);
        // Asegurarnos de que el terciario y el de subtablas se reseteen correctamente
        if (color === "#004434") {
          this.customStylesForm.get('tertiaryColor').patchValue(this.gesvatecColors.tertiaryColor);
          this.customStylesForm.get('subtableHeaderColor').patchValue(this.gesvatecColors.subtableHeaderColor);
        } else {
          const newMainLightColor = this.hexToRgba(color);
          const mainLightColorToPatch = this.rgbaToHex(newMainLightColor);
          this.customStylesForm.get('tertiaryColor').patchValue(mainLightColorToPatch);
          this.customStylesForm.get('subtableHeaderColor').patchValue(color);
        }
      }
    })

    // Por defecto, al seleccionar un color para la cabecera de las tablas, se aplica también en las subtablas
    this.customStylesForm.get('tableHeaderColor').valueChanges.pipe(takeUntil(this.destroy$)).subscribe( color => {
      if (this.avoidResettingRelatedColors) {
        this.avoidResettingRelatedColors = false;
        return;
      }
      if (color && color.length === 7) {
        // Asegurarnos de que el color de las subtablas se resetee correctamente
        if (color === "#004434") {
          this.customStylesForm.get('subtableHeaderColor').patchValue(this.gesvatecColors.subtableHeaderColor);
        } else {
          this.customStylesForm.get('subtableHeaderColor').patchValue(color);
        }
      }
    })

    // En caso de que se refresque la página o se cierre sesión, aplicar estilos custom guardados
    this.applyLocalStorageCustomColors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  public get imgSrc() {
    let src;
    if (localStorage.getItem("customStyles")) {
      const newCustomStyles = JSON.parse(localStorage.getItem("customStyles"));
      this.customStylesToSave.id = newCustomStyles.id;

      if (this.updateInitialCustomStylesValues) {
        this.customStylesForm.patchValue({
          primaryColor: newCustomStyles.primaryColor ? newCustomStyles.primaryColor : this.gesvatecColors.primaryColor,
          secondaryColor: newCustomStyles.secondaryColor ? newCustomStyles.secondaryColor : this.gesvatecColors.secondaryColor,
          tertiaryColor: newCustomStyles.tertiaryColor ? newCustomStyles.tertiaryColor : this.gesvatecColors.tertiaryColor,
          tableHeaderColor: newCustomStyles.tableHeaderColor ? newCustomStyles.tableHeaderColor : this.gesvatecColors.tableHeaderColor,
          subtableHeaderColor: newCustomStyles.subtableHeaderColor ? newCustomStyles.subtableHeaderColor : this.gesvatecColors.subtableHeaderColor
        })
        this.customStylesToSave.centralLogo = newCustomStyles.centralLogo;
        this.customStylesToSave.topBarLogo = newCustomStyles.topBarLogo;
        this.customStylesToSave.spinnerLogo = newCustomStyles.spinnerLogo;
        this.customStylesToSave.spinnerDashboardLogo = newCustomStyles.spinnerDashboardLogo;
        this.updateInitialCustomStylesValues = false;
      }

      if (newCustomStyles.topBarLogo?.src) {
        src = newCustomStyles.topBarLogo.src;
      } else {
        src = 'assets/img/logoBanner.png';
      }
    } else {
      src = 'assets/img/logoBanner.png';

      if (this.updateInitialCustomStylesValues) {
        // Asegurarnos de que al resetear los estilos y abrir de nuevo el modal, se muestren los colores por defecto
        this.customStylesForm.patchValue({
          primaryColor: this.gesvatecColors.primaryColor,
          secondaryColor: this.gesvatecColors.secondaryColor,
          tertiaryColor: this.gesvatecColors.tertiaryColor,
          tableHeaderColor: this.gesvatecColors.tableHeaderColor,
          subtableHeaderColor: this.gesvatecColors.subtableHeaderColor
        })
        this.updateInitialCustomStylesValues = false;
      }
    }
    return src;
  }

  /**
   * El campo de la db devuelve una string con el siguiente formato: "10h 50m".
   * Siempre es una agrupación de dos unidades de tiempo (año-mes, mes-día, día-hora, hora-minutos, minutos-segundos).
   * Necesitamos solo la unidad mayor.
   */
  private prepareNotificationEnviadoHace(period: string) {
    let periodoTiempo;
    if (period.includes('a')) {
      periodoTiempo =  'Hace ' + period.split('a')[0] + (period.split('a')[0] === '1' ? ' año' : ' años');
    }
    if (period.includes('y')) {
      periodoTiempo = period.split('y')[0] + (period.split('y')[0] === '1' ? ' year' : ' years') +  ' ago';
    }
    if (period.includes('m') && period.includes('d')) {
      if (this.languageWeb === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('m')[0] + (period.split('m')[0] === '1' ? ' mes' : ' meses');
      } else {
        periodoTiempo = period.split('m')[0] + (period.split('m')[0] === '1' ? ' month' : ' months') +  ' ago';
      }
    }
    if (period.includes('d') && period.includes('h')) {
      if (this.languageWeb === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('d')[0] + (period.split('d')[0] === '1' ? ' día' : ' días');
      } else {
        periodoTiempo = period.split('d')[0] + (period.split('d')[0] === '1' ? ' day' : ' days') +  ' ago';
      }
    }
    if (period.includes('h') && period.includes('m')) {
      if (this.languageWeb === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('h')[0] + (period.split('h')[0] === '1' ? ' hora' : ' horas');
      } else {
        periodoTiempo = period.split('h')[0] + (period.split('h')[0] === '1' ? ' hour' : ' hours') +  ' ago';
      }
    }
    if (period.includes('m') && period.includes('s')) {
      if (this.languageWeb === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('m')[0] + (period.split('m')[0] === '1' ? ' minuto' : ' minutos');
      } else {
        periodoTiempo = period.split('m')[0] + (period.split('m')[0] === '1' ? ' minute' : ' minutes') +  ' ago';
      }
    }
    return periodoTiempo;
  }

  public loadLanguage(langChange) {
    this._translate.get("languages").subscribe((data: any) => {
      this.languages.forEach((l) => {
        l.des = data[l.cultureInfo];
      });
      this.defaultLanguage = this.languages.find(
        (a) => a.cultureInfo == langChange || a.cultureInfo.includes(langChange)
      );
      this.showChangeLanguage = true;
      this.optionsLanguage = this.languages.filter(
        (a) => a.cultureInfo != this.defaultLanguage.cultureInfo
      );
    });
  }

  public get changePasswordModal() {
    const currentUrl = this._router.url.toString();
    const urlParams = this._router.parseUrl(this._router.url);
    if (currentUrl.includes("PasswordRecovery")) {
      this.userEmail = urlParams.queryParams.UserName;
      this.userGuid = urlParams.queryParams.PasswordRecovery;
    }

    return currentUrl.includes("PasswordRecovery");
  }

  public get confirmRegistrationModal() {
    const currentUrl = this._router.url.toString();
    const urlParams = this._router.parseUrl(this._router.url);
    if (currentUrl.includes("AppUserSolicitudRegistro")) {
      this.userGuid = urlParams.queryParams.AppUserSolicitudRegistro;
    }
    return currentUrl.includes("AppUserSolicitudRegistro");
  }

  public get languageWeb() {
    return this._translate.getDefaultLang();
  }

  public get angularVersion() {
    return packageJson.version;
  }

  public get version() {
    return this._sessionContext.version;
  }

  public get isSessionActive() {
    return this._sessionContext.isSessionActive();
  }

  public get sessionUser() {
    return this._sessionContext.sessionUser;
  }

  public openAppInfoModal() {
    this.showAppInfoModal = true;
  }

  public openChangePasswordModal() {
    this.showChangePasswordModal = true;
  }

  public openChangeLanguageModal() {
    this.showChangeLanguageModal = true;
  }

  public openEmailSubscriptionModal() {
    this.showOptions = false;
    this.showEmailSubscriptionModal = true;
  }

  public openModal() {
    if (!this.isSessionActive) {
      this.loginModal = true;
    } else {
      this.logoutModal = true;
    }
  }

  public closeModal(close) {
    this.loginModal = close;
    this.chngPsswdCloseModal = close;
    this.showChangePasswordModal = close;
    this.showChangeLanguageModal = close;
    this.showCustomStylesModal = close;
    this.showEmailSubscriptionModal = close;
  }

  public closeOptions(visible) {
    this.showOptions = visible;
  }

  public closeChangePwdModal(close) {
    this.chngPsswdCloseModal = close;
    this.showChangePasswordModal = close;
    this.showChangeLanguageModal = close;
  }

  public logout() {
    this._sessionService.logout();
  }

  public onChangeLanguage(idx) {
    this.showLanguageOptions = false;
    this.selectedLanguage = this.optionsLanguage[idx];
    this._translate.setDefaultLang(this.selectedLanguage.cultureInfo);
    this._translate.use(this.selectedLanguage.cultureInfo);
    this.loadLanguage(this.selectedLanguage.cultureInfo);
  }

  public get etiquetadores() {
    return this._dataProvider.etiquetadores;
  }

  public get entidadesControl() {
    return this._dataProvider.entidadesControl;
  }

  public get role() {
    return this._sessionContext.sessionUser.role;
  }

  public get claims() {
    return this._sessionContext.sessionUser.lsClaimTypes;
  }

  // Para saber si debemos crear o actualizar
  private get savedCustomStyles() {
    let styles;
    if (localStorage.getItem("customStyles")) {
      styles = JSON.parse(localStorage.getItem("customStyles"));
    } else {
      styles = null;
    }

    return styles;
  }

  public pushMessage(mensaje) {
    this.showError = true;
    this.mensajes.push(mensaje);
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
      this._spinnerService.hideSpinner();
    }
  }

  public openDocHelp() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._sessionService
      .getDocHelp("General.pdf")
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        localStorage.setItem("base64", resp.base64Archivo);
        const url = this._router.serializeUrl(
          this._router.createUrlTree(["viewPdf"])
        );
        window.open(url, "_blank");
      });
  }

  public onShowOptions() {
    this.showOptions = !this.showOptions;
    this.showNotifications = false;
    if (
      this.role === "Etiquetador" &&
      this.showOptions &&
      this.showOptionsDownload
    )
      this.showOptionsDownload = !this.showOptionsDownload;
  }

  public onShowOptionsDownload() {
    this.showOptionsDownload = !this.showOptionsDownload;
    this.showNotifications = false;
    if (
      this.role === "Etiquetador" &&
      this.showOptions &&
      this.showOptionsDownload
    )
      this.showOptions = !this.showOptions;
  }

  public onShowNotifications() {
    this.showNotifications = !this.showNotifications;
    this.showOptions = false;
  }

  public goToNotificationsHub(notificationId: number) {
    this._sessionContext.notificationIdOnInit = notificationId;
    this.showNotifications = false;
  }

  // option 1: view, option 2: download
  public onViewPDF(file: string, option: number) {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    const fileDownload =
      file === "software"
        ? "Labeling_Software_User_Manual.pdf"
        : "Labeling_Software_Installation.pdf";
    this._sessionService
      .getFileDownload(fileDownload)
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        if (option === 1) {
          localStorage.setItem("base64", resp.base64Archivo);
          const url = this._router.serializeUrl(
            this._router.createUrlTree(["viewPdf"])
          );
          window.open(url, "_blank");
        } else {
          const downloadLink = document.createElement("a");
          downloadLink.href =
            "data:application/pdf;base64," + resp.base64Archivo;
          downloadLink.download = resp.nombreArchivo;
          downloadLink.click();
        }
      });
  }

  public onDownloadSoftware(type: string) {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    const file =
      type === "64"
        ? "Setup_Gesvatec_7500_x64.exe"
        : "Setup_Gesvatec_7500_x86.exe";
    this._sessionService
      .getFileDownload(file)
      .toPromise()
      .finally(() => {
        this.checkCloseSpinner();
      })
      .then((resp) => {
        const downloadLink = document.createElement("a");
        downloadLink.href =
          "data:application/x-msdownload;base64," + resp.base64Archivo;
        downloadLink.download = resp.nombreArchivo;
        downloadLink.click();
      });
  }

  // ------**---*-  Personalización de estilos -*---**------
  public openCustomStylesModal() {
    this.showOptions = false;
    // Antes de abrir el modal, reseteamos todos los valores de los input file
    this.customStylesForm.get('centralLogo')?.patchValue(null);
    this.customStylesForm.get('topBarLogo')?.patchValue(null);
    this.customStylesForm.get('spinnerDashboardLogo')?.patchValue(null);
    this.customStylesForm.get('spinnerLogo')?.patchValue(null);
    this.showCustomStylesModal = true;
    this.updateInitialCustomStylesValues = true;
  }

  public resetAllStyles() {
    this.customStylesForm.patchValue({
      primaryColor: this.gesvatecColors.primaryColor,
      secondaryColor: this.gesvatecColors.secondaryColor,
      tertiaryColor: this.gesvatecColors.tertiaryColor,
      tableHeaderColor: this.gesvatecColors.tableHeaderColor,
      subtableHeaderColor: this.gesvatecColors.subtableHeaderColor
    });

    this.customStylesToSave = {
      id: null,
      primaryColor: null,
      secondaryColor: null,
      tertiaryColor: null,
      tableHeaderColor: null,
      subtableHeaderColor: null,
      centralLogo: null,
      topBarLogo: null,
      spinnerLogo: null,
      spinnerDashboardLogo: null,
    }
  }

  public resetConcreteStyle(controlName: string) {
    switch (controlName) {
      case "primaryColor":
        this.avoidResettingRelatedColors = true;
        this.customStylesForm.patchValue({ primaryColor: this.gesvatecColors.primaryColor });
        this.customStylesToSave.primaryColor = null;
        break;
      case "secondaryColor":
        this.customStylesForm.patchValue({ secondaryColor: this.gesvatecColors.secondaryColor });
        this.customStylesToSave.secondaryColor = null;
        break;
      case "tertiaryColor":
        this.customStylesForm.patchValue({ tertiaryColor: this.gesvatecColors.tertiaryColor });
        this.customStylesToSave.tertiaryColor = null;
        break;
      case "tableHeaderColor":
        this.avoidResettingRelatedColors = true;
        this.customStylesForm.patchValue({
          tableHeaderColor: this.gesvatecColors.tableHeaderColor,
        });
        this.customStylesToSave.tableHeaderColor = null;
        break;
      case "subtableHeaderColor":
        this.customStylesForm.patchValue({
          subtableHeaderColor: this.gesvatecColors.subtableHeaderColor,
        });
        this.customStylesToSave.subtableHeaderColor = null;
        break;
      case "centralLogo":
        this.customStylesToSave.centralLogo = null;
        this.customStylesForm.get('centralLogo').patchValue(null);
        break;
      case "topBarLogo":
        this.customStylesToSave.topBarLogo = null;
        this.customStylesForm.get('topBarLogo').patchValue(null);
        break;
      case "spinnerLogo":
        this.customStylesToSave.spinnerLogo = null;
        this.customStylesForm.get('spinnerLogo').patchValue(null);
        break;
      case "spinnerDashboardLogo":
        this.customStylesToSave.spinnerDashboardLogo = null;
        this.customStylesForm.get('spinnerDashboardLogo').patchValue(null);
        break;
    }
  }

  private applyLocalStorageCustomColors() {
    if (localStorage.getItem("customStyles")) {
      const newCustomStyles = JSON.parse(localStorage.getItem("customStyles"));

      if (newCustomStyles.primaryColor) {
        document.documentElement.style.setProperty(
          "--main-color",
          newCustomStyles.primaryColor
        );
      }
      if (newCustomStyles.secondaryColor) {
        document.documentElement.style.setProperty(
          "--secondary-color",
          newCustomStyles.secondaryColor
        );
      }
      if (newCustomStyles.tertiaryColor) {
        document.documentElement.style.setProperty(
          "--main-light-color",
          newCustomStyles.tertiaryColor
        );
      }
      if (newCustomStyles.tableHeaderColor) {
        document.documentElement.style.setProperty(
          "--table-header-color",
          newCustomStyles.tableHeaderColor
        );
      }
      if (newCustomStyles.subtableHeaderColor) {
        document.documentElement.style.setProperty(
          "--subtable-header-color",
          newCustomStyles.subtableHeaderColor
        );
      }
    }
  }

  private applyCustomTheme(styleId?: number) {
    if (this.customStylesForm.get("primaryColor").value &&
    this.customStylesForm.get("primaryColor").value !==
      this.gesvatecColors.primaryColor) {
      document.documentElement.style.setProperty(
        "--main-color",
        this.customStylesToSave.primaryColor
      );
    } else {
      document.documentElement.style.removeProperty("--main-color");
    }
    
    if (this.customStylesForm.get("secondaryColor").value &&
    this.customStylesForm.get("secondaryColor").value !==
      this.gesvatecColors.secondaryColor) {
      document.documentElement.style.setProperty(
        "--secondary-color",
        this.customStylesToSave.secondaryColor
      );
    } else {
      document.documentElement.style.removeProperty("--secondary-color");
    }

    if (this.customStylesForm.get("tertiaryColor").value &&
    this.customStylesForm.get("tertiaryColor").value !==
      this.gesvatecColors.tertiaryColor) {
      document.documentElement.style.setProperty(
        "--main-light-color",
        this.customStylesToSave.tertiaryColor
      );
    } else {
      document.documentElement.style.removeProperty("--main-light-color");
    }

    if (this.customStylesForm.get("tableHeaderColor").value &&
    this.customStylesForm.get("tableHeaderColor").value !==
      this.gesvatecColors.tableHeaderColor) {
      document.documentElement.style.setProperty(
        "--table-header-color",
        this.customStylesToSave.tableHeaderColor
      );
    } else {
      document.documentElement.style.removeProperty("--table-header-color");
    }

    if (this.customStylesForm.get("subtableHeaderColor").value &&
    this.customStylesForm.get("subtableHeaderColor").value !==
      this.gesvatecColors.subtableHeaderColor) {
      document.documentElement.style.setProperty(
        "--subtable-header-color",
        this.customStylesToSave.subtableHeaderColor
      );
    } else {
      document.documentElement.style.removeProperty("--subtable-header-color");
    }

    // Si se estaba creando
    if (!this.customStylesToSave.id) {
      this.customStylesToSave.id = styleId;
    }
    
    // Guardado en localStorage de todos los estilos
    if (!localStorage.getItem("customStyles")) {
      localStorage.setItem("customStyles", JSON.stringify(this.customStylesToSave));
    } else {
      localStorage.removeItem("customStyles");
      localStorage.setItem("customStyles", JSON.stringify(this.customStylesToSave));
    }
  }

  private resetToDefaultTheme() {
    document.documentElement.style.removeProperty("--main-color");
    document.documentElement.style.removeProperty("--secondary-color");
    document.documentElement.style.removeProperty("--main-light-color");
    document.documentElement.style.removeProperty("--table-header-color");
    document.documentElement.style.removeProperty("--subtable-header-color");

    localStorage.removeItem("customStyles");
    this.customStylesToSave.id = null;
  }

  public createNewCustomStyle() {
    this.mensajes = [];
    const jsonString = JSON.stringify(this.customStylesToSave);
      const filters = {
        des: this.entidadesControl[0].des,
        estilo: jsonString,
        entidadControlId: this.entidadesControl[0].id
      };
    this._spinnerService.showSpinner();
    this._sessionService.createCustomStyle(filters).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(
      (resp) => {
        this.applyCustomTheme(resp.id);
        this.showCustomStylesModal = false;
        this.showError = true;
        this.mensajes.push({
          class: "success",
          message: "session.customStyles.success.aplicados",
        });
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      },
      (error) => {
        this.showCustomStylesModal = false;
        this.showError = true;
        this.mensajes.push({
          class: "error",
          message: "global.error.noResponse",
        });
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      }
    )
  }

  public updateExistingCustomStyle() {
    this.mensajes = [];
    const jsonString = JSON.stringify(this.customStylesToSave);
      const filters = {
        des: this.entidadesControl[0].des,
        estilo: jsonString,
        entidadControlId: this.entidadesControl[0].id,
        id: this.savedCustomStyles.id
      };
    this._spinnerService.showSpinner();
    this._sessionService.updateCustomStyle(filters).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(
      (resp) => {
        this.applyCustomTheme();
        this.showCustomStylesModal = false;
        this.showError = true;
        this.mensajes.push({
          class: "success",
          message: "session.customStyles.success.aplicados",
        });
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      },
      (error) => {
        this.showCustomStylesModal = false;
        this.showError = true;
        if (error.status === 420) {
          switch (error.error.ApiErrorCode) {
            case 6228:
              this.mensajes.push({
                class: "error",
                message: "session.customStyles.errors.noAccesible",
              });
              break;
            default: 
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          }
        } else {
          this.mensajes.push({
            class: "error",
            message: "global.error.noResponse",
          });
        }
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      }
    )
  }

  public deleteExistingCustomStyle() {
    this.mensajes = [];
    const styleId = this.savedCustomStyles.id;
    this._spinnerService.showSpinner();
    this._sessionService.deleteCustomStyle(styleId).toPromise().finally(() => {
      this._spinnerService.hideSpinner();
    }).then(
      (resp) => {
        this.resetToDefaultTheme();
        this.showCustomStylesModal = false;
        this.showError = true;
        this.mensajes.push({
          class: "success",
          message: "session.customStyles.success.aplicados",
        });
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      },
      (error) => {
        this.showCustomStylesModal = false;
        this.showError = true;
        if (error.status === 420) {
          switch (error.error.ApiErrorCode) {
            case 6228:
              this.mensajes.push({
                class: "error",
                message: "session.customStyles.errors.noAccesible",
              });
              break;
            default: 
              this.mensajes.push({
                class: "error",
                message: "global.error.noResponse",
              });
          }
        } else {
          this.mensajes.push({
            class: "error",
            message: "global.error.noResponse",
          });
        }
        setTimeout(() => {
          this.mensajes = [];
        }, 5000);
      }
    )
  }

  private prepareCustomStylesBeforeCreating() {
    this.customStylesToSave = {
      id: this.customStylesToSave.id,
      // Solo pasamos los valores si son diferentes a los estilos por defecto de Gesvatec
      primaryColor:
        this.customStylesForm.get("primaryColor").value &&
        this.customStylesForm.get("primaryColor").value !==
          this.gesvatecColors.primaryColor
          ? this.customStylesForm.get("primaryColor").value
          : null,
      secondaryColor:
        this.customStylesForm.get("secondaryColor").value &&
        this.customStylesForm.get("secondaryColor").value !==
          this.gesvatecColors.secondaryColor
          ? this.customStylesForm.get("secondaryColor").value
          : null,
      tertiaryColor:
        this.customStylesForm.get("tertiaryColor").value &&
        this.customStylesForm.get("tertiaryColor").value !==
          this.gesvatecColors.tertiaryColor
          ? this.customStylesForm.get("tertiaryColor").value
          : null,
      tableHeaderColor:
        this.customStylesForm.get("tableHeaderColor").value &&
        this.customStylesForm.get("tableHeaderColor").value !==
          this.gesvatecColors.tableHeaderColor
          ? this.customStylesForm.get("tableHeaderColor").value
          : null,
      subtableHeaderColor:
        this.customStylesForm.get("subtableHeaderColor").value &&
        this.customStylesForm.get("subtableHeaderColor").value !==
          this.gesvatecColors.subtableHeaderColor
          ? this.customStylesForm.get("subtableHeaderColor").value
          : null,
      // Lás imágenes de este objeto ya tienen o valor null o la string en base64
      centralLogo: this.customStylesToSave.centralLogo,
      topBarLogo: this.customStylesToSave.topBarLogo,
      spinnerLogo: this.customStylesToSave.spinnerLogo,
      spinnerDashboardLogo: this.customStylesToSave.spinnerDashboardLogo,
    };
  }

  public confirmCustomStyles() {
    // Evitar que el usuario pueda seleccionar blanco puro para el color principal y secundario
    if (this.customStylesForm.get('primaryColor').value === '#ffffff' || this.customStylesForm.get('secondaryColor').value === '#ffffff') {
      this.showError = true;
      this.mensajes.push({
        class: "error",
        message: "session.customStyles.errors.white",
      });
      return;
    }
    this.prepareCustomStylesBeforeCreating();
    // Comprobamos que el único campo con valor no sea el id
    const atLeastOnePropertyExists = Object.keys(this.customStylesToSave).some(key => this.customStylesToSave[key] !== null && typeof this.customStylesToSave[key] !== 'number');

    // Crear
    if (atLeastOnePropertyExists && !this.savedCustomStyles) {
      this.createNewCustomStyle();
      // Actualizar
    } else if (atLeastOnePropertyExists && this.savedCustomStyles) {
      this.updateExistingCustomStyle();
      // Borrar
    } else if (!atLeastOnePropertyExists && this.savedCustomStyles) {
      this.deleteExistingCustomStyle();
    }
  }

  public onFileSelected(event: any, controlName: string) {
    const file = event.target?.files[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        const base64Image = reader.result as string;
        
        // Guardamos las imágenes en base64 en vez de actualizar el valor del input
        switch (controlName) {
          case 'centralLogo':
            this.customStylesToSave.centralLogo = {
              name: file.name,
              src: base64Image
            };
            this.customStylesToSave.spinnerLogo = {
              name: file.name,
              src: base64Image
            };
            this.customStylesToSave.spinnerDashboardLogo = {
              name: file.name,
              src: base64Image
            };
            break;
          case 'topBarLogo':
            this.customStylesToSave.topBarLogo = {
              name: file.name,
              src: base64Image
            };
            break;
          case 'spinnerLogo':
            this.customStylesToSave.spinnerLogo = {
              name: file.name,
              src: base64Image
            };
            this.customStylesToSave.spinnerDashboardLogo = {
              name: file.name,
              src: base64Image
            };
            break;
          case 'spinnerDashboardLogo':
            this.customStylesToSave.spinnerDashboardLogo = {
              name: file.name,
              src: base64Image
            };
            break;
        }
      };

      reader.readAsDataURL(file);
    }
  }

  public removeFile(controlName) {
    switch (controlName) {
      case 'centralLogo':
        this.customStylesToSave.centralLogo = null;
        this.customStylesForm.get('centralLogo').patchValue(null);
        break;
      case 'topBarLogo':
        this.customStylesToSave.topBarLogo = null;
        this.customStylesForm.get('topBarLogo').patchValue(null);
        break;
      case 'spinnerLogo':
        this.customStylesToSave.spinnerLogo = null;
        this.customStylesForm.get('spinnerLogo').patchValue(null);
        break;
      case 'spinnerDashboardLogo':
        this.customStylesToSave.spinnerDashboardLogo = null;
        this.customStylesForm.get('spinnerDashboardLogo').patchValue(null);
        break;
    }
  }

  public getFileName(controlName: string): string {
    const fileName = this.customStylesToSave[controlName]?.name;
    return fileName ? fileName : null;
  }

  private hexToRgba(hex: string, alpha: number = 0.3): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private rgbaToHex(rgba: string): string {
    // Extraer los valores RGB
    const result = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    const r = parseInt(result[1]);
    const g = parseInt(result[2]);
    const b = parseInt(result[3]);
    const alpha = result[4] ? parseFloat(result[4]) : 1;
    // Fondo blanco
    const backgroundR = 255;
    const backgroundG = 255;
    const backgroundB = 255;
    // Calcular la mezcla del color junto con la opacidad
    const blendedR = Math.round((r * alpha) + (backgroundR * (1 - alpha)));
    const blendedG = Math.round((g * alpha) + (backgroundG * (1 - alpha)));
    const blendedB = Math.round((b * alpha) + (backgroundB * (1 - alpha)));
    // Convertir de nuevo a hexadecimal
    const hexR = blendedR.toString(16).padStart(2, '0');
    const hexG = blendedG.toString(16).padStart(2, '0');
    const hexB = blendedB.toString(16).padStart(2, '0');
    return `#${hexR}${hexG}${hexB}`;
  }
}
